/**
 * AI Studio — the creative engine of Orkexra.
 *
 * Left: Creative Brief + template picker + mode toggle.
 * Right: Preview canvas with per-asset cards (edit / regenerate / save / send to review).
 * Bottom: Pipeline visualisation — reminds the user this isn't a chatbot.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Icons from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import type {
  CampaignPack,
  ContentTypeInfo,
  CreativeBriefIn,
  GeneratedAsset,
  StarterTemplate,
} from "@/entities/ai-studio";
import {
  PACK_PRESETS,
  createPack,
  fetchPack,
  fetchPackAssets,
  generateSingle,
  getAssetLabel,
  listContentTypes,
  listPipelineStages,
  listRecentPacks,
  listSavedBriefs,
  listTemplates,
  regenerateAsset,
  saveBrief as apiSaveBrief,
  sendAssetToReview,
  sendPackToReview,
  type BulkSendToReviewOut,
} from "@/entities/ai-studio";
import { useCurrentWorkspace } from "@/entities/workspace";
import { useUpgradePrompt } from "@/features/entitlements/useEntitlements";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Skeleton } from "@/shared/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Textarea } from "@/shared/ui/textarea";

const EMPTY_BRIEF: CreativeBriefIn = {
  objective: "",
  audience: "",
  offer: "",
  call_to_action: "",
  campaign: "",
  platforms: [],
  tone: "",
  length: "",
  notes: "",
  creativity: "balanced",
};

type Mode = "single" | "pack";

export function AIStudioPage(): JSX.Element {
  const ws = useCurrentWorkspace();
  const qc = useQueryClient();
  const upgrade = useUpgradePrompt();
  // Patch 37.4.42 · ``?pack=<id>`` deep link support so founders can
  // reach Send-all-to-review on any existing pack, not only the one
  // they just generated in this browser session (iter-76 finding).
  const [searchParams, setSearchParams] = useSearchParams();
  const packIdParam = searchParams.get("pack") || null;

  const [mode, setMode] = useState<Mode>("pack");
  const [brief, setBrief] = useState<CreativeBriefIn>(EMPTY_BRIEF);
  const [selectedType, setSelectedType] = useState<string>("social_facebook");
  const [selectedPreset, setSelectedPreset] = useState<string>("full_awareness");
  const [customTypes, setCustomTypes] = useState<string[]>([]);
  const [activePack, setActivePack] = useState<CampaignPack | null>(null);
  const [singleAsset, setSingleAsset] = useState<GeneratedAsset | null>(null);
  const [saveName, setSaveName] = useState("");

  const templatesQ = useQuery({ queryKey: ["ai-studio-templates"], queryFn: listTemplates });
  const typesQ = useQuery({ queryKey: ["ai-studio-content-types"], queryFn: listContentTypes });
  const stagesQ = useQuery({ queryKey: ["ai-studio-pipeline"], queryFn: listPipelineStages });
  const memoryQ = useQuery({
    queryKey: ["ai-memory", ws?.id],
    queryFn: () => listSavedBriefs(ws!.id),
    enabled: !!ws,
  });

  // Reset panel state when workspace changes.
  // Patch 37.4.42 iter-77 fix (HIGH) · This effect must NOT fire on
  // the initial mount — that used to strip the ``?pack=<id>`` deep
  // link before ``useCurrentWorkspace`` had finished bootstrapping,
  // rendering the deep link permanently broken. Track the previous
  // ws.id in a ref and only reset (+ clear the param) on an actual
  // ws→ws transition.
  const prevWsIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const wsId = ws?.id;
    // First run OR ws still bootstrapping: seed the ref, do not clear.
    if (prevWsIdRef.current === undefined) {
      prevWsIdRef.current = wsId;
      return;
    }
    if (prevWsIdRef.current === wsId) return;
    prevWsIdRef.current = wsId;
    setActivePack(null);
    setSingleAsset(null);
    setBrief(EMPTY_BRIEF);
    // Clear any pack deep-link — the pack id is ws-scoped, so keeping
    // it across a ws switch would 404 or (worse) land on a foreign row.
    if (searchParams.has("pack")) {
      const next = new URLSearchParams(searchParams);
      next.delete("pack");
      setSearchParams(next, { replace: true });
    }
  }, [ws?.id, searchParams, setSearchParams]);

  // Patch 37.4.42 · ``?pack=<id>`` — one-shot hydrate. When the URL
  // names a pack the founder wants to work on, fetch it, land in
  // Pack mode, and set it as ``activePack`` so the Send-all button
  // + counts + assets grid all reference it. Only fires when the
  // param is present AND we don't already own the pack — a bare
  // re-render must not re-fetch.
  const packFromLinkQ = useQuery({
    queryKey: ["campaign-pack", packIdParam, "hydrate"],
    queryFn: () => fetchPack(ws!.id, packIdParam!),
    enabled: !!ws && !!packIdParam && activePack?.id !== packIdParam,
  });
  useEffect(() => {
    if (packFromLinkQ.data && packFromLinkQ.data.id !== activePack?.id) {
      setActivePack(packFromLinkQ.data);
      setMode("pack");
    }
  }, [packFromLinkQ.data, activePack?.id]);
  // Keep the URL in sync when the founder generates a NEW pack in
  // session so a refresh preserves context (bookmark-friendly).
  useEffect(() => {
    if (!activePack) return;
    if (searchParams.get("pack") === activePack.id) return;
    const next = new URLSearchParams(searchParams);
    next.set("pack", activePack.id);
    setSearchParams(next, { replace: true });
  }, [activePack, searchParams, setSearchParams]);

  const packQ = useQuery({
    queryKey: ["campaign-pack", activePack?.id],
    queryFn: () => fetchPack(ws!.id, activePack!.id),
    enabled: !!activePack && activePack.status === "generating",
    refetchInterval: (q) =>
      (q.state.data as CampaignPack | undefined)?.status === "generating" ? 2500 : false,
  });
  const packAssetsQ = useQuery({
    queryKey: ["campaign-pack-assets", activePack?.id],
    queryFn: () => fetchPackAssets(ws!.id, activePack!.id),
    enabled: !!activePack,
    refetchInterval: () => (packQ.data?.status === "generating" ? 2500 : false),
  });

  const genSingle = useMutation({
    mutationFn: () => generateSingle(ws!.id, { content_type: selectedType, brief }),
    onSuccess: (a) => {
      setSingleAsset(a);
      void qc.invalidateQueries({ queryKey: ["ai-studio-history", ws?.id] });
      toast.success("Asset generated");
    },
    onError: (e: unknown) => {
      const err = e as { status?: number; code?: string; message?: string; details?: Record<string, unknown> };
      if (err.status === 402 || err.code === "text_generation_allowance_exhausted") {
        upgrade.open({
          title: "Monthly text capacity reached",
          reason: err.message || "Upgrade for unlimited text generation or wait for the monthly reset.",
          upgrade_target: err.details?.upgrade_target as string | undefined,
        });
        return;
      }
      toast.error(err.message || "Generation failed");
    },
  });

  const genPack = useMutation({
    mutationFn: () =>
      createPack(ws!.id, {
        brief,
        preset: selectedPreset === "custom" ? undefined : selectedPreset,
        content_types: selectedPreset === "custom" ? customTypes : undefined,
      }),
    onSuccess: (p) => {
      setActivePack(p);
      toast.success(`Pack started — ${p.total_assets} assets generating`);
    },
    onError: (e: unknown) => toast.error((e as Error).message || "Pack failed to start"),
  });

  const regen = useMutation({
    mutationFn: (assetId: string) => regenerateAsset(ws!.id, assetId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-pack-assets", activePack?.id] });
      toast.success("Regenerated");
    },
    onError: (e: unknown) => {
      const err = e as { status?: number; code?: string; message?: string; details?: Record<string, unknown> };
      if (err.status === 402 || err.code === "text_generation_allowance_exhausted") {
        upgrade.open({
          title: "Monthly text capacity reached",
          reason: err.message || "Upgrade for unlimited text generation or wait for the monthly reset.",
          upgrade_target: err.details?.upgrade_target as string | undefined,
        });
        return;
      }
      toast.error(err.message || "Regenerate failed");
    },
  });

  const sendReview = useMutation({
    mutationFn: (assetId: string) => sendAssetToReview(ws!.id, assetId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-pack-assets", activePack?.id] });
      toast.success("Sent to review");
    },
  });

  const sendPack = useMutation({
    mutationFn: () => sendPackToReview(ws!.id, activePack!.id),
    onSuccess: (r: BulkSendToReviewOut) => {
      void qc.invalidateQueries({ queryKey: ["campaign-pack-assets", activePack?.id] });
      void qc.invalidateQueries({ queryKey: ["campaign-pack", activePack?.id] });
      // Patch 37.4.42 · Per-item summary — never lies about partial
      // failures. All-skipped repeats also read cleanly.
      // Patch 37.4.44 · include ``needs_recovery`` so the toast
      // never claims completion when malformed heads were skipped,
      // and downgrade the tone to a warning when nothing moved.
      const parts: string[] = [];
      if (r.sent > 0) parts.push(`${r.sent} sent`);
      if (r.skipped_not_draft > 0) parts.push(`${r.skipped_not_draft} already in review`);
      if (r.skipped_locked > 0) parts.push(`${r.skipped_locked} locked`);
      const needsRecovery = r.needs_recovery ?? 0;
      if (needsRecovery > 0) parts.push(`${needsRecovery} need recovery`);
      if (r.errors > 0) parts.push(`${r.errors} failed`);
      const summary = parts.length ? parts.join(" · ") : "Nothing to send";
      if (r.errors > 0) {
        toast.error(`Send to review — ${summary}`);
      } else if (r.sent === 0 && (needsRecovery > 0 || r.skipped_locked > 0)) {
        toast.warning(`Send to review — ${summary}`);
      } else {
        toast.success(`Send to review — ${summary}`);
      }
    },
    onError: (e: unknown) => toast.error((e as Error).message || "Bulk send failed"),
  });

  const saveBriefMut = useMutation({
    mutationFn: () => apiSaveBrief(ws!.id, saveName || `Brief ${new Date().toLocaleString()}`, brief),
    onSuccess: () => {
      setSaveName("");
      void qc.invalidateQueries({ queryKey: ["ai-memory", ws?.id] });
      toast.success("Brief saved to AI Memory");
    },
  });

  if (!ws) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <p className="text-muted-foreground">Select a workspace to open AI Studio.</p>
      </div>
    );
  }

  const applyTemplate = (t: StarterTemplate) => {
    setBrief({
      objective: t.objective,
      audience: t.audience ?? "",
      offer: t.offer ?? "",
      call_to_action: t.call_to_action ?? "",
      campaign: t.campaign ?? "",
      platforms: t.platforms ?? [],
      tone: t.tone ?? "",
      length: "",
      notes: t.notes ?? "",
      creativity: "balanced",
    });
    setSelectedPreset(t.suggested_preset);
    setMode("pack");
    toast.success(`Template applied — ${t.label}`);
  };

  const assets = (packAssetsQ.data ?? []).slice().sort((a, b) => a.content_type.localeCompare(b.content_type));

  return (
    <div className="mx-auto max-w-7xl px-6 py-10" data-testid="ai-studio-page">
      <Header ws={ws} />

      <div className="grid gap-6 lg:grid-cols-[420px_minmax(0,1fr)]">
        {/* Left: Brief panel */}
        <aside className="space-y-4">
          <TemplateGrid templates={templatesQ.data ?? []} loading={templatesQ.isLoading} onPick={applyTemplate} />

          <Card className="border-border/70 bg-card/70 backdrop-blur">
            <CardHeader>
              <CardTitle className="font-display text-lg">Creative Brief</CardTitle>
              <CardDescription className="text-xs">
                Tell us what to accomplish. The platform builds the prompts internally.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <FieldText testId="ai-studio-objective" label="Objective *" rows={2} value={brief.objective}
                         onChange={(v) => setBrief({ ...brief, objective: v })} />
              <FieldText testId="ai-studio-audience" label="Audience" value={brief.audience ?? ""}
                         onChange={(v) => setBrief({ ...brief, audience: v })} />
              <FieldText testId="ai-studio-offer" label="Offer" value={brief.offer ?? ""}
                         onChange={(v) => setBrief({ ...brief, offer: v })} />
              <FieldText testId="ai-studio-cta" label="Call to action" value={brief.call_to_action ?? ""}
                         onChange={(v) => setBrief({ ...brief, call_to_action: v })} />
              <FieldText testId="ai-studio-campaign" label="Campaign" value={brief.campaign ?? ""}
                         onChange={(v) => setBrief({ ...brief, campaign: v })} />
              <FieldText testId="ai-studio-tone" label="Tone nudge" value={brief.tone ?? ""}
                         onChange={(v) => setBrief({ ...brief, tone: v })} />
              <FieldText testId="ai-studio-notes" label="Notes" rows={2} value={brief.notes ?? ""}
                         onChange={(v) => setBrief({ ...brief, notes: v })} />

              <div className="flex items-center gap-2 pt-1">
                <Input
                  data-testid="ai-studio-save-name"
                  placeholder="Save this brief as…"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                />
                <Button variant="outline" size="sm" data-testid="ai-studio-save-brief"
                        disabled={!brief.objective || saveBriefMut.isPending}
                        onClick={() => saveBriefMut.mutate()}>
                  Save
                </Button>
              </div>

              {(memoryQ.data?.length ?? 0) > 0 && (
                <div className="pt-3">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">AI Memory</Label>
                  <div className="mt-2 space-y-1">
                    {(memoryQ.data ?? []).slice(0, 5).map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        data-testid={`ai-memory-recall-${m.id}`}
                        onClick={() =>
                          setBrief({
                            objective: m.objective,
                            audience: m.audience ?? "",
                            offer: m.offer ?? "",
                            call_to_action: m.call_to_action ?? "",
                            campaign: m.campaign ?? "",
                            platforms: m.platforms ?? [],
                            tone: m.tone ?? "",
                            length: m.length ?? "",
                            notes: m.notes ?? "",
                            creativity: m.creativity ?? "balanced",
                          })
                        }
                        className="w-full rounded-md border border-border/60 bg-background/40 px-3 py-2 text-left text-xs hover:bg-secondary/60"
                      >
                        <div className="font-medium">{m.name}</div>
                        <div className="truncate text-[10px] text-muted-foreground">{m.objective}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/70 bg-card/70 backdrop-blur">
            <CardHeader>
              <CardTitle className="font-display text-lg">Generation Mode</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
                <TabsList className="grid grid-cols-2">
                  <TabsTrigger value="pack" data-testid="ai-studio-mode-pack">Campaign Pack</TabsTrigger>
                  <TabsTrigger value="single" data-testid="ai-studio-mode-single">Single Asset</TabsTrigger>
                </TabsList>
                <TabsContent value="pack" className="space-y-2 pt-3">
                  {PACK_PRESETS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      data-testid={`ai-studio-pack-preset-${p.key}`}
                      className={`w-full rounded-md border px-3 py-2 text-left text-xs transition ${
                        selectedPreset === p.key
                          ? "border-primary bg-primary/10"
                          : "border-border/60 bg-background/40 hover:bg-secondary/60"
                      }`}
                      onClick={() => setSelectedPreset(p.key)}
                    >
                      <div className="font-medium">{p.label}</div>
                      <div className="text-[10px] text-muted-foreground">{p.hint}</div>
                    </button>
                  ))}
                  {selectedPreset === "custom" && (
                    <CustomTypePicker types={typesQ.data ?? []} selected={customTypes} onChange={setCustomTypes} />
                  )}
                </TabsContent>
                <TabsContent value="single" className="pt-3">
                  <ContentTypePicker types={typesQ.data ?? []} selected={selectedType} onChange={setSelectedType} />
                </TabsContent>
              </Tabs>

              <Button
                data-testid="ai-studio-generate"
                className="w-full"
                disabled={!brief.objective || genSingle.isPending || genPack.isPending}
                onClick={() => (mode === "single" ? genSingle.mutate() : genPack.mutate())}
              >
                {(genSingle.isPending || genPack.isPending) ? "Generating…" : mode === "single" ? "Generate" : "Generate Campaign Pack"}
              </Button>
            </CardContent>
          </Card>
        </aside>

        {/* Right: Preview canvas */}
        <section className="space-y-4">
          {mode === "pack" ? (
            <PackPanel
              pack={packQ.data ?? activePack}
              assets={assets}
              onRegenerate={(id) => regen.mutate(id)}
              onSendReview={(id) => sendReview.mutate(id)}
              regenPending={regen.isPending}
              onSendPack={() => sendPack.mutate()}
              sendPackPending={sendPack.isPending}
            />
          ) : (
            <SinglePanel
              asset={singleAsset}
              onRegenerate={(id) => regen.mutate(id)}
              onSendReview={(id) => sendReview.mutate(id)}
              regenPending={regen.isPending}
            />
          )}

          {/* Patch 37.4.43 · Recent Generations — persistent, refresh-
              survives, workspace-scoped list of the most recent packs.
              Closes the "unreachable after refresh" symptom flagged in
              37.4.42 iter-78. Each row links to
              ``/ai-studio/packs/{id}`` so bulk Send-to-Review is
              always one click away, regardless of session state. */}
          {ws ? <RecentGenerations workspaceId={ws.id} /> : null}

          <PipelineVisual stages={stagesQ.data ?? []} loading={stagesQ.isLoading} />
        </section>
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────

function Header({ ws }: { ws: { slug: string; profile: { display_name: string; primary_color: string } } }): JSX.Element {
  return (
    <div className="mb-8 space-y-2">
      <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/60 px-3 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: ws.profile.primary_color }} />
        {ws.profile.display_name} · ai studio
      </div>
      <h1 data-testid="ai-studio-title" className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">
        {ws.profile.display_name} · <span style={{ color: ws.profile.primary_color }}>AI Studio</span>
      </h1>
      <p className="max-w-2xl text-sm text-muted-foreground md:text-base">
        Describe an objective. The platform orchestrates Brand Brain, prompts, and providers to build a coordinated campaign — every asset saved as a draft.
      </p>
    </div>
  );
}

function TemplateGrid({
  templates, loading, onPick,
}: { templates: StarterTemplate[]; loading: boolean; onPick: (t: StarterTemplate) => void }): JSX.Element {
  if (loading) return <Skeleton className="h-40 w-full" />;
  return (
    <Card className="border-border/70 bg-card/70 backdrop-blur">
      <CardHeader>
        <CardTitle className="font-display text-lg">Starter Templates</CardTitle>
        <CardDescription className="text-xs">
          Tap a template to prefill the Creative Brief below. You can edit every field before generating.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2">
          {templates.map((t) => {
            const Icon = (Icons as unknown as Record<string, Icons.LucideIcon>)[
              (t.icon || "sparkles").replace(/-([a-z])/g, (_, ch) => ch.toUpperCase()).replace(/^./, (c) => c.toUpperCase())
            ] ?? Icons.Sparkles;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`ai-studio-template-${t.key}`}
                onClick={() => onPick(t)}
                className="group flex items-start gap-2 rounded-md border border-border/60 bg-background/40 px-3 py-2 text-left text-xs hover:bg-secondary/60"
              >
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <div className="font-medium">{t.label}</div>
                  <div className="line-clamp-2 text-[10px] text-muted-foreground">{t.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function FieldText({
  testId, label, value, onChange, rows,
}: { testId: string; label: string; value: string; onChange: (v: string) => void; rows?: number }): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      {rows ? (
        <Textarea data-testid={testId} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function ContentTypePicker({
  types, selected, onChange,
}: { types: ContentTypeInfo[]; selected: string; onChange: (v: string) => void }): JSX.Element {
  const grouped: Record<string, ContentTypeInfo[]> = {};
  types.forEach((t) => (grouped[t.category] ??= []).push(t));
  return (
    <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <div className="mb-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{cat.replace("_", " ")}</div>
          <div className="grid grid-cols-1 gap-1">
            {items.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`ai-studio-content-type-${t.key}`}
                onClick={() => onChange(t.key)}
                className={`rounded-md border px-3 py-1.5 text-left text-xs transition ${
                  selected === t.key ? "border-primary bg-primary/10" : "border-border/60 bg-background/40 hover:bg-secondary/60"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomTypePicker({
  types, selected, onChange,
}: { types: ContentTypeInfo[]; selected: string[]; onChange: (v: string[]) => void }): JSX.Element {
  const toggle = (k: string) =>
    onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  return (
    <div className="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-md border border-border/60 p-2">
      {types.map((t) => (
        <label key={t.key} className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            data-testid={`ai-studio-custom-type-${t.key}`}
            checked={selected.includes(t.key)}
            onChange={() => toggle(t.key)}
          />
          {t.label}
        </label>
      ))}
    </div>
  );
}

// Patch 37.4.43 · exported so the new ``/ai-studio/packs/:packId``
// detail route can reuse the exact same rendering discipline (Send-
// all button + counts + progress-bar visibility) without
// duplicating any UI logic.
export function PackPanel({
  pack, assets, onRegenerate, onSendReview, regenPending, onSendPack, sendPackPending,
}: {
  pack: CampaignPack | null;
  assets: GeneratedAsset[];
  onRegenerate: (id: string) => void;
  onSendReview: (id: string) => void;
  regenPending: boolean;
  onSendPack: () => void;
  sendPackPending: boolean;
}): JSX.Element {
  if (!pack) {
    return (
      <Card data-testid="ai-studio-empty-state" className="border-border/70 bg-card/70 backdrop-blur">
        <CardHeader>
          <CardTitle className="font-display text-lg">No campaign yet</CardTitle>
          <CardDescription>Describe an objective, pick a preset, and hit generate.</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  // Patch 37.4.44 · Canonical breakdown is derived from the SERVER's
  // ``live_by_status`` / ``live_total`` / ``live_hidden`` — never from
  // the in-memory ``assets.length`` filter. The old client-derived
  // path silently under-counted a pack containing a malformed head
  // (the assets grid dropped it) and under-reported drafts. Every
  // surface (Recent Generations, Pack Detail headline, Ops card) now
  // reads the same server-derived counts so headlines cannot drift.
  const draftCount = pack.live_by_status?.draft ?? 0;
  const reviewCount = pack.live_by_status?.needs_review ?? 0;
  const approvedCount = pack.live_by_status?.approved ?? 0;
  const approvedVaulted = pack.live_approved_vaulted ?? 0;
  const hiddenCount = pack.live_hidden ?? 0;
  const liveTotal = pack.live_total ?? pack.total_assets;
  // Patch 37.4.44 · Truthful CTA gating. Malformed heads can carry a
  // raw ``status = "draft"`` so ``live_by_status.draft`` over-counts
  // when hidden rows exist. Send-to-review would emit ``needs_recovery``
  // for those and return ``sent = 0`` — but the CTA has already told
  // the founder N drafts are ready to move. Subtract ``hiddenCount``
  // (upper-bounded by ``draftCount`` so the counter never goes
  // negative) so the button label and enablement match what the
  // server will actually process.
  const sendableDrafts = Math.max(draftCount - hiddenCount, 0);
  // Progress bar — only meaningful while the fan-out is running.
  // iter-77 fix (LOW): a stale-counter pack would otherwise render a
  // 0%-wide bar next to a header saying "1 assets". Hide the bar
  // once generation has settled.
  const generating = pack.status === "generating";
  const generatedCount = assets.length; // rows already written to ai_assets
  const pct = generating
    ? Math.round(
        ((generatedCount + pack.failed_assets) /
          Math.max(pack.total_assets, generatedCount, 1)) *
          100,
      )
    : 100;
  const canBulkSend = !generating && sendableDrafts > 0;
  return (
    <Card data-testid={`campaign-pack-card-${pack.id}`} className="border-border/70 bg-card/70 backdrop-blur">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-display text-lg">{pack.objective}</CardTitle>
            <CardDescription className="text-xs" data-testid={`campaign-pack-header-${pack.id}`}>
              {pack.preset ?? "custom"} · {liveTotal} assets{pack.total_cost_usd != null ? ` · $${pack.total_cost_usd.toFixed(4)}` : ""} · {pack.status}
            </CardDescription>
          </div>
          <Badge data-testid="campaign-pack-status" variant="outline" className="font-mono text-[10px] uppercase tracking-widest">
            {pack.status}
          </Badge>
        </div>
        {generating ? (
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        ) : null}
        <div
          className="mt-3 flex flex-wrap items-center gap-2"
          data-testid={`campaign-pack-toolbar-${pack.id}`}
        >
          <div
            className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
            data-testid={`campaign-pack-counts-${pack.id}`}
          >
            {draftCount} draft · {reviewCount} in review · {approvedCount} approved
            {approvedVaulted > 0 ? ` (${approvedVaulted} in vault)` : ""}
          </div>
          {hiddenCount > 0 ? (
            <span
              data-testid={`campaign-pack-needs-recovery-${pack.id}`}
              className="inline-flex items-center gap-1 rounded-md border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-amber-500"
              title="Some items in this pack could not be loaded and need recovery."
            >
              {hiddenCount} {hiddenCount === 1 ? "item" : "items"} need recovery
            </span>
          ) : null}
          <div className="ml-auto">
            <Button
              size="sm"
              variant="secondary"
              data-testid={`ai-studio-pack-send-all-review-${pack.id}`}
              disabled={!canBulkSend || sendPackPending}
              onClick={onSendPack}
            >
              {sendPackPending ? "Sending…" : `Send all to review${sendableDrafts ? ` (${sendableDrafts})` : ""}`}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-2">
        {assets.map((a, i) => (
          <AssetCard
            key={a.id}
            asset={a}
            index={i}
            onRegenerate={onRegenerate}
            onSendReview={onSendReview}
            regenPending={regenPending}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function SinglePanel({
  asset, onRegenerate, onSendReview, regenPending,
}: {
  asset: GeneratedAsset | null;
  onRegenerate: (id: string) => void;
  onSendReview: (id: string) => void;
  regenPending: boolean;
}): JSX.Element {
  if (!asset) {
    return (
      <Card data-testid="ai-studio-empty-state" className="border-border/70 bg-card/70 backdrop-blur">
        <CardHeader>
          <CardTitle className="font-display text-lg">Nothing generated yet</CardTitle>
          <CardDescription>Pick a content type and generate a single asset.</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  return <AssetCard asset={asset} index={0} onRegenerate={onRegenerate} onSendReview={onSendReview} regenPending={regenPending} />;
}

function AssetCard({
  asset, index, onRegenerate, onSendReview, regenPending,
}: {
  asset: GeneratedAsset;
  index: number;
  onRegenerate: (id: string) => void;
  onSendReview: (id: string) => void;
  regenPending: boolean;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const body = useMemo(() => {
    if (asset.body_json) return JSON.stringify(asset.body_json, null, 2);
    if (asset.image_prompt) return asset.image_prompt;
    return asset.body_markdown ?? "";
  }, [asset]);
  const isJson = !!asset.body_json;
  const truncated = body.length > 300 && !expanded;
  // Patch 37.4.44 · needs_recovery placeholders render with a
  // dedicated card state. The row exists (stable id + status) but
  // its payload could not be loaded; the customer sees "needs
  // recovery" and lifecycle actions are disabled until the row is
  // repaired via the founder diagnostic.
  if (asset.needs_recovery) {
    return (
      <Card
        data-testid={`ai-studio-asset-card-${index}`}
        className="border-amber-400/50 bg-amber-500/5"
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-amber-500">
                {asset.category || "unknown"}
              </div>
              <CardTitle className="text-sm">
                {getAssetLabel(asset)}
              </CardTitle>
            </div>
            <Badge
              data-testid={`ai-studio-asset-needs-recovery-${index}`}
              variant="outline"
              className="border-amber-400/50 font-mono text-[10px] uppercase tracking-widest text-amber-500"
            >
              Needs recovery
            </Badge>
          </div>
          <CardDescription className="text-[10px]">
            This item exists in the pack but its content could not be loaded. Recovery is required before it can be reviewed or approved.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              data-testid={`ai-studio-asset-regenerate-${index}`}
              variant="outline"
              size="sm"
              disabled
            >
              Regenerate
            </Button>
            <Button
              data-testid={`ai-studio-asset-send-review-${index}`}
              variant="secondary"
              size="sm"
              disabled
            >
              Send to review
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid={`ai-studio-asset-card-${index}`} className="border-border/60 bg-background/50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {asset.category}
            </div>
            <CardTitle className="text-sm">{getAssetLabel(asset)}</CardTitle>
          </div>
          <Badge data-testid={`ai-studio-asset-status-${index}`} variant="outline" className="font-mono text-[10px] uppercase tracking-widest">
            {asset.status}
          </Badge>
        </div>
        <CardDescription className="text-[10px]">
          {asset.provider ?? "—"}{asset.model ? ` · ${asset.model}` : ""}{asset.estimated_cost_usd != null ? ` · $${asset.estimated_cost_usd.toFixed(5)}` : ""}{asset.latency_ms != null ? ` · ${Math.round(asset.latency_ms)}ms` : ""}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {asset.error ? (
          <div className="rounded-md border border-red-500/50 bg-red-500/10 p-2 text-xs text-red-700 dark:text-red-300">
            {asset.error}
          </div>
        ) : (
          <pre
            data-testid={`ai-studio-asset-body-${index}`}
            className={`whitespace-pre-wrap break-words rounded-md bg-secondary/40 p-3 font-sans text-xs ${isJson ? "font-mono" : ""}`}
          >
            {truncated ? body.slice(0, 300) + "…" : body || "(empty)"}
          </pre>
        )}
        {body.length > 300 && (
          <button type="button" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
                  onClick={() => setExpanded((v) => !v)}>
            {expanded ? "collapse" : "expand"}
          </button>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button data-testid={`ai-studio-asset-regenerate-${index}`}
                  variant="outline" size="sm"
                  disabled={regenPending}
                  onClick={() => onRegenerate(asset.id)}>
            Regenerate
          </Button>
          <Button data-testid={`ai-studio-asset-send-review-${index}`}
                  variant="secondary" size="sm"
                  disabled={asset.status === "needs_review"}
                  onClick={() => onSendReview(asset.id)}>
            {asset.status === "needs_review" ? "In review" : "Send to review"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PipelineVisual({
  stages, loading,
}: { stages: { key: string; label: string; description: string }[]; loading: boolean }): JSX.Element {
  if (loading) return <Skeleton className="h-24 w-full" />;
  return (
    <Card data-testid="ai-studio-pipeline" className="border-border/70 bg-card/70 backdrop-blur">
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-sm uppercase tracking-widest text-muted-foreground">
          Pipeline
        </CardTitle>
        <CardDescription className="text-xs">Orkexra orchestrates every generation through this chain.</CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="flex flex-wrap items-center gap-1.5">
          {stages.map((s, i) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span
                data-testid={`ai-studio-pipeline-stage-${s.key}`}
                title={s.description}
                className="rounded-full border border-border/60 bg-background/40 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
              >
                {s.label}
              </span>
              {i < stages.length - 1 && <span className="text-muted-foreground/50">›</span>}
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

// Patch 37.4.43 · Persistent Recent Generations panel — the founder's
// primary way to return to a pack they generated earlier. Every row
// is a link to ``/ai-studio/packs/{id}`` so Send-all-to-review and
// Review Session are one click away regardless of session state.
// Counts are all live-derived (``live_total``/``live_by_status``);
// the campaign name is server-resolved via
// ``pack_stats_service._resolve_campaign_name`` — the client renders
// verbatim, cannot drift.
function RecentGenerations({ workspaceId }: { workspaceId: string }): JSX.Element {
  const q = useQuery({
    queryKey: ["ai-studio-recent-packs", workspaceId],
    queryFn: () => listRecentPacks(workspaceId, 8),
  });
  if (q.isLoading) {
    return (
      <Card
        data-testid="ai-studio-recent-generations-loading"
        className="border-border/70 bg-card/70 backdrop-blur"
      >
        <CardHeader className="pb-2">
          <CardTitle className="font-display text-sm uppercase tracking-widest text-muted-foreground">
            Recent Generations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }
  const packs = q.data ?? [];
  if (packs.length === 0) return <></>;
  return (
    <Card
      data-testid="ai-studio-recent-generations"
      className="border-border/70 bg-card/70 backdrop-blur"
    >
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-sm uppercase tracking-widest text-muted-foreground">
          Recent Generations
        </CardTitle>
        <CardDescription className="text-xs">
          Return to any pack you generated recently. Send-all-to-review + Review Session live inside.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {packs.map((p) => {
          const total = p.live_total ?? p.total_assets;
          const drafts = p.live_by_status?.draft ?? 0;
          const review = p.live_by_status?.needs_review ?? 0;
          const hidden = p.live_hidden ?? 0;
          const label = p.campaign_name_resolved ?? p.objective ?? "Untitled pack";
          return (
            <Link
              key={p.id}
              to={`/ai-studio/packs/${p.id}`}
              data-testid={`ai-studio-recent-pack-${p.id}`}
              className="group flex items-center justify-between gap-3 rounded-md border border-border/60 bg-background/40 px-3 py-2 text-xs transition hover:border-border hover:bg-background/70"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{label}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  <span>{total} assets</span>
                  {drafts > 0 && (
                    <span
                      data-testid={`ai-studio-recent-pack-${p.id}-drafts`}
                      className="text-amber-500"
                    >
                      {drafts} draft
                    </span>
                  )}
                  {review > 0 && (
                    <span
                      data-testid={`ai-studio-recent-pack-${p.id}-review`}
                      className="text-cyan-500"
                    >
                      {review} in review
                    </span>
                  )}
                  {hidden > 0 && (
                    <span
                      data-testid={`ai-studio-recent-pack-${p.id}-needs-recovery`}
                      className="text-amber-500"
                    >
                      {hidden} {hidden === 1 ? "item" : "items"} need recovery
                    </span>
                  )}
                  <span className="text-muted-foreground/70">· {p.status}</span>
                </div>
              </div>
              <Icons.ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-foreground" />
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
}
